import React from "react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import CardSlider from "./ServicesCardSlider"; 

const ScrollSection = () => {
  const services = [
    { name: "Face Recognition", description: "Advanced AI for facial analysis" },
    { name: "OCR", description: "Text extraction from images" },
    { name: "Object Detection", description: "Identifying objects seamlessly" },
    { name: "Text Summarization", description: "Turning text into key ideas" },
    { name: "Language Translation", description: "AI-powered translation" },
    { name: "Sentiment Analysis", description: "Understanding emotions in data" },
  ];

  const scatteredWords = [
    { text: "Innovation", top: "15%", left: "10%" },
    { text: "AI", top: "25%", left: "50%" },
    { text: "Vision", top: "35%", left: "30%" },
    { text: "Future", top: "50%", left: "65%" },
    { text: "Neural", top: "60%", left: "75%" },
    { text: "Networks", top: "70%", left: "20%" },
    { text: "Solutions", top: "80%", left: "55%" },
  ];

  const scatteredtinyWords = [
    { text: "AI Vision", top: "15%", left: "30%" },
    { text: "AI Innovation", top: "25%", left: "50%" },
    { text: "AI Research", top: "35%", left: "10%" },
    { text: "AI Future", top: "50%", left: "65%" },
    { text: "AI Revolution", top: "60%", left: "25%" },
  ];

  const cardimages = [
    { src: "/assets/img8.avif", text: "AI Vision" },
    { src: "/assets/img9.avif", text: "AI Innovation" },
    { src: "/assets/img10.jpg", text: "AI Research" },
    { src: "/assets/img11.avif", text: "AI Future" },
    { src: "/assets/img7.avif", text: "AI Revolution" },
  ];

  // Keep images in a closer cluster
  const getRandomPosition = () => ({
    top: `${30 + Math.random() * 30}vh`, // Keep images in the center region
    left: `${25 + Math.random() * 35}vw`, // Keep within viewport
  });

  // Vary image sizes slightly for depth effect
  const getRandomSize = () => {
    const sizes = ["w-24 h-24", "w-28 h-28", "w-32 h-32", "w-36 h-36"];
    return sizes[Math.floor(Math.random() * sizes.length)];
  };

  const ConnectedWords = () => {
    const [particles, setParticles] = useState([
      { text: "AI Vision", x: Math.random() * 80, y: Math.random() * 80, vx: 0.05, vy: 0.03 },
      { text: "AI Innovation", x: Math.random() * 80, y: Math.random() * 80, vx: -0.07, vy: 0.04 },
      { text: "AI Research", x: Math.random() * 80, y: Math.random() * 80, vx: 0.06, vy: -0.02 },
      { text: "AI Future", x: Math.random() * 80, y: Math.random() * 80, vx: -0.03, vy: 0.06 },
      { text: "AI Revolution", x: Math.random() * 80, y: Math.random() * 80, vx: 0.04, vy: -0.05 },
    ]);
  
    useEffect(() => {
      const interval = setInterval(() => {
        setParticles((prevParticles) =>
          prevParticles.map((particle) => {
            let newX = particle.x + particle.vx;
            let newY = particle.y + particle.vy;
            let newVx = particle.vx;
            let newVy = particle.vy;
  
            // Bounce off walls
            if (newX < 10 || newX > 90) newVx = -newVx;
            if (newY < 10 || newY > 90) newVy = -newVy;
  
            return { ...particle, x: newX, y: newY, vx: newVx, vy: newVy };
          })
        );
      }, 50);
  
      return () => clearInterval(interval);
    }, []);
  
    const getDistance = (p1: any, p2: any) => {
      return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    };
  
    const shouldConnect = (p1: any, p2: any) => {
      return getDistance(p1, p2) < 30;
    };
  
    return (
      <div className="relative h-screen w-full">
        {/* Connection lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
          {particles.map((p1, i) =>
            particles.slice(i + 1).map((p2, j) => {
              if (shouldConnect(p1, p2)) {
                return (
                  <motion.line
                    key={`line-${i}-${j}`}
                    x1={`${p1.x}%`}
                    y1={`${p1.y}%`}
                    x2={`${p2.x}%`}
                    y2={`${p2.y}%`}
                    stroke="white"
                    strokeWidth="0.5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.2 }}
                    className="animate-pulse-slow"
                  />
                );
              }
              return null;
            })
          )}
        </svg>
  
        {/* Particles with text */}
        {particles.map((particle, index) => (
          <motion.div
            key={index}
            className="absolute flex flex-col items-center justify-center"
            style={{
              top: `${particle.y}%`,
              left: `${particle.x}%`,
              transform: "translate(-50%, -50%)",
            }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            {/* Glowing dot */}
            <motion.div
              className="w-2 h-2 bg-white rounded-full"
              style={{
                boxShadow: "0 0 10px rgba(255, 255, 255, 0.7)",
              }}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.3, 0.7, 0.3],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
  
            {/* Text label */}
            <span className="text-sm font-medium text-white/60 whitespace-nowrap tracking-wider uppercase mt-2">
              {particle.text}
            </span>
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <div className="relative bg-black text-white min-h-screen overflow-hidden">
      {/* Faint Watermark Styled Words */}
      {scatteredWords.map((word, index) => (
        <motion.div
          key={index}
          className="absolute text-[2rem] md:text-[3rem] font-extrabold opacity-20 text-gray-500 uppercase pointer-events-none"
          style={{
            top: word.top,
            left: word.left,
          }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.2, scale: 1 }} // Reduced opacity for subtle watermark effect
          transition={{ duration: 1.2, delay: index * 0.3 }}
        >
          {word.text}
        </motion.div>
      ))}

      {/* Main Service Titles & Description */}
      <div className="container mx-auto flex flex-col items-center py-16 relative z-10">
        {services.map((service, index) => (
          <motion.div
            key={index}
            className="w-full flex flex-col md:flex-row justify-between items-center py-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: index * 0.2 }}
            viewport={{ once: false, amount: 0.5 }}
          >
            <motion.h2
              className="text-5xl md:text-7xl font-bold uppercase text-white"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {service.name}
            </motion.h2>
            <motion.p
              className="text-lg text-gray-300 mt-4 md:mt-0 max-w-md text-center md:text-right"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {service.description}
            </motion.p>
          </motion.div>
        ))}
      </div>

      {/* Scattered Tiny AI Images */}
      <div className="relative h-screen w-full bg-black flex items-center justify-center overflow-hidden">
        <ConnectedWords />
        {cardimages.map((image, index) => {
          const randomPosition = getRandomPosition();
          const randomSize = getRandomSize();
          const textOffset = Math.random() * 100 - 90;

          return (
            <div key={`group-${index}`}>
            <motion.div
              key={index}
              className={`absolute rounded-xl shadow-lg ${randomSize}`}
              style={randomPosition}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 1,
                delay: index * 0.3,
                ease: "easeOut",
              }}
            >
              <img src={image.src} alt={image.text} className="w-full h-full object-cover rounded-xl" />
            </motion.div>

          </div>
          );
        })}
      </div>

      {/* AI Service Cards (Sliding) */}
      <CardSlider />
    </div>
  );
};

export default ScrollSection;





























// import React from "react";
// import { motion } from "framer-motion";
// import CardSlider from "./ServicesCardSlider"; // Import CardSlider

// const ScrollSection = () => {
//   const services = [
//     { name: "Face Recognition", description: "Advanced AI for facial analysis" },
//     { name: "OCR", description: "Text extraction from images" },
//     { name: "Object Detection", description: "Identifying objects seamlessly" },
//     { name: "Text Summarization", description: "Turning text into key ideas" },
//     { name: "Language Translation", description: "AI-powered translation" },
//     { name: "Sentiment Analysis", description: "Understanding emotions in data" },
//   ];

//   const images = [
//     { src: "/assets/img7.avif", text: "AI-Powered " },
//     { src: "/assets/explore.webp", text: "" },
//     { src: "/assets/img2.jpg", text: "AI-Powered" },
//   ];

//   const scatteredWords = [
//     { text: "Innovation", top: "10%", left: "15%" },
//     { text: "AI", top: "20%", left: "50%" },
//     { text: "Vision", top: "30%", left: "25%" },
//     { text: "Future", top: "40%", left: "70%" },
//     { text: "Neural", top: "55%", left: "80%" },
//     { text: "Networks", top: "65%", left: "20%" },
//     { text: "Solutions", top: "75%", left: "50%" },
//   ];

//   const cardimages = [
//     { src: "/assets/img8.avif", text: "AI Vision" },
//     { src: "/assets/img9.avif", text: "AI Innovation" },
//     { src: "/assets/img10.avif", text: "AI Research" },
//     { src: "/assets/img11.avif", text: "AI Future" },
//     { src: "/assets/img7.avif", text: "AI Revolution" },
//   ];
  
//   const getRandomPosition = () => ({
//     top: `${20 + Math.random() * 30}vh`, // Restrict vertical spread
//     left: `${20 + Math.random() * 40}vw`, // Restrict horizontal spread
//   });
  
//   const getRandomSize = () => {
//     const sizes = ["w-16 h-16", "w-20 h-20", "w-20 h-20", "w-28 h-28", "w-32 h-32"];
//     return sizes[Math.floor(Math.random() * sizes.length)];
//   };

//   const smoothTransition = {
//     initial: { opacity: 0, y: 50 },
//     whileInView: { opacity: 1, y: 0 },
//     transition: { duration: 0.8 },
//     viewport: { once: false, amount: 0.5 },
//   };

//   return (
//     <div className="relative bg-black text-black min-h-screen overflow-x-hidden">
//       {/* Scattered Words Styled Like Neural Abyss */}
//       {scatteredWords.map((word, index) => (
//         <motion.div
//           key={index}
//           className="absolute text-[2rem] md:text-[3rem] font-extrabold opacity-5 text-gray-300 uppercase pointer-events-none"
//           style={{
//             top: word.top,
//             left: word.left,
//           }}
//           initial={{ opacity: 0, scale: 0.8 }}
//           animate={{ opacity: 0.5, scale: 1 }}
//           transition={{ duration: 1.2, delay: index * 0.3 }}
//         >
//           {word.text}
//         </motion.div>
//       ))}

//       {/* Main Section Title */}

//       {/* Transition Section with Text */}
//       <div className="container mx-auto flex flex-col items-center py-16 relative z-10">
//         {services.map((service, index) => (
//           <motion.div
//             key={index}
//             className="w-full flex flex-col md:flex-row justify-between items-center py-16"
//             {...smoothTransition}
//           >
//             <motion.h2
//               className="text-5xl md:text-7xl font-bold uppercase text-white"
//               {...smoothTransition}
//             >
//               {service.name}
//             </motion.h2>
//             <motion.p
//               className="text-lg text-gray-100 mt-4 md:mt-0 max-w-md text-center md:text-right"
//               {...smoothTransition}
//             >
//               {service.description}
//             </motion.p>
//           </motion.div>
//         ))}
//       </div>

//       {/* Transitioning Images with Overlaid Text */}

//     <div className="relative h-screen w-full bg-black flex items-center justify-center overflow-hidden">
//       {images.map((image, index) => {
//         const randomPosition = getRandomPosition();
//         const randomSize = getRandomSize();

//         return (
//           <motion.div
//             key={index}
//             className={`absolute rounded-xl shadow-lg ${randomSize}`}
//             style={randomPosition}
//             initial={{ opacity: 0, scale: 0.5 }}
//             animate={{ opacity: 1, scale: 1 }}
//             transition={{
//               duration: 1,
//               delay: index * 0.3,
//               ease: "easeOut",
//             }}
//           >
//             <img src={image.src} alt={image.text} className="w-full h-full object-cover rounded-xl" />
//           </motion.div>
//         );
//       })}
//     </div>

//       {/* Use CardSlider for the Card Section */}
//       <CardSlider />
//     </div>
//   );
// };

// export default ScrollSection;











      {/* <div className="relative h-screen w-full bg-black z-10">
        {images.map((image, index) => (
          <motion.div
            key={index}
            className="absolute inset-0 flex justify-center items-center"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: index * 0.4 }}
            viewport={{ once: false, amount: 0.5 }}
          >
            <div className="relative">
              <img
                src={image.src}
                alt={`AI Transition ${index}`}
                className="w-2/4 h-1/4 object-cover rounded-lg shadow-lg"
              />
              <motion.div
                className="absolute inset-0 flex justify-center items-center text-white text-3xl font-bold"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                {image.text}
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div> */}